/* eslint-disable import/order */

// polyfills
import 'core-js'

import {
	createGenerateClassName,
	createTheme,
	jssPreset,
	MuiThemeProvider,
	StylesProvider
} from '@material-ui/core/styles'
import { ConnectedRouter } from 'connected-react-router'
import { create } from 'jss'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { PersistGate } from 'redux-persist/integration/react'
import { getDefaultDependentSagas, setDependentSagas, startup } from 'studiokit-scaffolding-js'
import AccessibleAppComponent from 'studiokit-scaffolding-js/lib/components/HOC/AccessibleAppComponent'
import { registerQuill } from 'studiokit-scaffolding-js/lib/components/Quill'
import { ReduxState } from './types/ReduxState'
import * as serviceWorker from 'studiokit-scaffolding-js/lib/utils/serviceWorker'

//#region CSS

import 'bootstrap/dist/css/bootstrap.min.css'
import 'react-image-crop/dist/ReactCrop.css'
import 'studiokit-scaffolding-js/lib/css/variables.css'
import './css/variables.css'
import 'studiokit-scaffolding-js/lib/css/index.css'
import './css/index.css'

// import tachyons after global app styles
// this allows tachyons, which are set inline, to be applied over equally specific selectors
// e.g. if global `.alert` has a padding, then `.pa3` is added inline, the tachyon overrides the padding
// NOTE: component specific styles are still imported after tachyons, and take precedence
import 'tachyons/css/tachyons.min.css'

//#endregion CSS

//#region globals

// @ts-ignore: vite does not provide node global value like create-react-app did.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
window.global ||= window

//#endregion globals

//#region Quill

registerQuill()

//#endregion Quill

//#region JSS + material-ui

const generateClassName = createGenerateClassName()
const insertionPoint = document.getElementById('jss-insertion-point')
const jss = create({
	...jssPreset(),
	// We define a custom insertion point that JSS will look for injecting the styles in the DOM.
	insertionPoint: insertionPoint || '#jss-insertion-point'
})

const theme = createTheme({
	palette: {
		primary: {
			main: '#581c87'
		},
		error: {
			main: '#C94349'
		},
		success: {
			main: '#7bcf64'
		}
	},
	typography: {
		fontFamily: [
			'Plus Jakarta Sans',
			'"Helvetica Neue Light"',
			'"Helvetica Neue"',
			'Helvetica',
			'Arial',
			'sans-serif'
		].join(',')
	},
	shape: {
		borderRadius: 20
	}
})

//#endregion JSS + material-ui

//#region App

// (imported after other css)
import App from './components/App'

// Note: override TOwnProps generic, since all `AppProps` are provided by the internal `connect()`
const Application = AccessibleAppComponent<RouteComponentProps>(App)

//#endregion App

//#region Scaffolding

import configuration from './configuration'
import endpointMappings from './endpointMappings'

// exclude default data sagas from dependentSagas
const { initialDataLoadSaga, postLoginDataSaga, caliperSaga, ...dependentSagas } = getDefaultDependentSagas()
setDependentSagas(dependentSagas)

const containerElementId = 'root'
const { history, store, persistor } = startup<ReduxState>(configuration, endpointMappings)

//#endregion Scaffolding Startup

ReactDOM.render(
	<Provider store={store} key="provider">
		<PersistGate loading={null} persistor={persistor}>
			<StylesProvider jss={jss} generateClassName={generateClassName}>
				<MuiThemeProvider theme={theme}>
					<ConnectedRouter history={history}>
						<Application />
					</ConnectedRouter>
				</MuiThemeProvider>
			</StylesProvider>
		</PersistGate>
	</Provider>,
	document.getElementById(containerElementId)
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister()
